import { useEffect, useRef, useState, type RefObject } from 'react';
import type { TimeBounds } from './timeRangeDomain';

/** Plot height every machine chart shares, so switching views never moves the controls below */
export const CHART_PLOT_HEIGHT = 160;

/** Where the vertical grid lines sit, as fractions of the plot width; one per X-axis label */
export const X_GRID_FRACTIONS = [0, 1 / 3, 2 / 3, 1] as const;

/** Colours the machine charts share; text keeps the text tokens, marks use these. */
export const CHART_COLORS = {
  grid: 'rgb(231, 234, 238)',
  track: 'rgb(244, 246, 248)',
  on: 'rgb(123, 187, 243)',
  threshold: 'rgb(234, 88, 12)',
  /** The red the single-machine bars paint past a threshold */
  breach: 'rgb(239, 68, 68)',
  crosshair: 'rgb(131, 136, 142)',
  noData: 'rgb(131, 136, 142)',
  surface: '#ffffff',
} as const;

/** Width of the element the ref is attached to, kept current on resize. */
export function useContainerWidth<T extends HTMLElement>(
  initialWidth = 400
): [RefObject<T | null>, number] {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(initialWidth);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const update = () => setWidth(element.clientWidth);
    update();

    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return [ref, width];
}

export function timeToX(time: number, bounds: TimeBounds, width: number) {
  if (bounds.to === bounds.from) return width / 2;
  return ((time - bounds.from) / (bounds.to - bounds.from)) * width;
}

export function xToTime(x: number, bounds: TimeBounds, width: number) {
  if (width <= 0) return bounds.from;
  return bounds.from + (x / width) * (bounds.to - bounds.from);
}

/** Tooltip time: clock time for windows up to two days, day and clock beyond. */
export function formatPointerTime(time: number, bounds: TimeBounds) {
  const date = new Date(time);
  const clock = date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const hours = (bounds.to - bounds.from) / (60 * 60 * 1000);
  if (hours <= 48) return clock;

  const day = date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
  });
  return `${day} ${clock}`;
}

/** Legend line for the machines that have no sample in the window */
export function describeMachinesWithoutData(count: number) {
  return `${count} machine${count === 1 ? '' : 's'} without data`;
}

export function formatValue(value: number) {
  return value.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

/**
 * Y scale with rounded ticks. Mirrors the single-machine bar chart so the
 * per-machine view and the single view agree on where a value sits.
 */
export function computeYScale(dataMin: number, dataMax: number) {
  if (!Number.isFinite(dataMin) || !Number.isFinite(dataMax)) {
    return { yMin: 0, yMax: 1, ticks: [0, 1] };
  }

  const padding = (dataMax - dataMin) * 0.1 || 5;
  let yMin = Math.floor(dataMin - padding);
  let yMax = Math.ceil(dataMax + padding);

  const range = yMax - yMin;
  let step: number;
  if (range <= 5) {
    step = 1;
  } else if (range <= 10) {
    step = 2;
  } else if (range <= 25) {
    step = 5;
  } else {
    step = Math.ceil(range / 5 / 10) * 10;
  }

  yMin = Math.floor(yMin / step) * step;
  yMax = Math.ceil(yMax / step) * step;

  const ticks: number[] = [];
  for (let value = yMin; value <= yMax; value += step) {
    ticks.push(value);
  }

  return { yMin, yMax, ticks };
}
